import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { loginRequest } from '@/auth/authConfig';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Toaster } from '@/components/ui/sonner';
import { UserProvider } from '@/contexts/UserContext';
import DashboardLayout from '@/layouts/DashboardLayout';
import AdminProductsPage from '@/pages/admin/AdminProductsPage';
import AdminProductDetailPage from '@/pages/admin/AdminProductDetailPage';
import AdminFeatureCatalogPage from '@/pages/admin/AdminFeatureCatalogPage';
import AdminOrganizationsPage from '@/pages/admin/AdminOrganizationsPage';
import AdminOrganizationDetailPage from '@/pages/admin/AdminOrganizationDetailPage';
import LegalDocumentsPage from '@/pages/admin/LegalDocumentsPage';
import LegalDocumentVersionDetailPage from '@/pages/admin/LegalDocumentVersionDetailPage';
import StripeCustomersPage from '@/pages/admin/stripe/StripeCustomersPage';
import StripeSubscriptionsPage from '@/pages/admin/stripe/StripeSubscriptionsPage';
import AdminStripeProductsPage from '@/pages/admin/AdminStripeProductsPage';
import AdminSystemNoticesPage from '@/pages/admin/AdminSystemNoticesPage';
import AdminSystemNoticeDetailPage from '@/pages/admin/AdminSystemNoticeDetailPage';
import AdminNotificationTemplatesPage from '@/pages/admin/AdminNotificationTemplatesPage';
import AdminNotificationTemplateDetailPage from '@/pages/admin/AdminNotificationTemplateDetailPage';
import AdminBroadcastDetailPage from '@/pages/admin/AdminBroadcastDetailPage';
import DashboardPage from '@/pages/DashboardPage';
import { logger } from '@/services/logger';

/**
 * Main application component with authentication flow
 */
function App() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  /**
   * Handle user sign-in
   */
  const handleLogin = () => {
    instance.loginPopup(loginRequest).catch((e) => {
      console.error('Login failed:', e);
      logger.error(e, { context: 'Login' });
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Mission Impact Administration</CardTitle>
            <CardDescription>
              Sign in to access the mission impact administration platform
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={handleLogin} className="w-full">
              Sign in with Microsoft
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <UserProvider>
      <BrowserRouter>
        <Toaster position="top-right" />
        <Routes>
          {/* Redirect root to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Dashboard layout with nested routes */}
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="admin/products" element={<AdminProductsPage />} />
            <Route path="admin/products/:productId" element={<AdminProductDetailPage />} />
            <Route path="admin/feature-catalog" element={<AdminFeatureCatalogPage />} />
            <Route path="admin/organizations" element={<AdminOrganizationsPage />} />
            <Route
              path="admin/organizations/:organizationId"
              element={<AdminOrganizationDetailPage />}
            />
            <Route path="admin/legal-documents" element={<LegalDocumentsPage />} />
            <Route
              path="admin/legal-documents/:type/versions/:version"
              element={<LegalDocumentVersionDetailPage />}
            />
            <Route path="admin/stripe/products" element={<AdminStripeProductsPage />} />
            <Route path="admin/stripe/customers" element={<StripeCustomersPage />} />
            <Route path="admin/stripe/subscriptions" element={<StripeSubscriptionsPage />} />
            <Route path="admin/system-notices" element={<AdminSystemNoticesPage />} />
            <Route
              path="admin/system-notices/:noticeId"
              element={<AdminSystemNoticeDetailPage />}
            />
            <Route
              path="admin/notifications/templates"
              element={<AdminNotificationTemplatesPage />}
            />
            <Route
              path="admin/notifications/templates/:templateId"
              element={<AdminNotificationTemplateDetailPage />}
            />
            <Route path="admin/broadcasts/:broadcastId" element={<AdminBroadcastDetailPage />} />
          </Route>

          {/* Catch all - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </UserProvider>
  );
}

export default App;
